/**
 * 주소 검색 — 다음(카카오) 우편번호 서비스.
 *
 * ## 왜 이것인가 (2026-08-27)
 * **키도 도메인 등록도 없다.** 지도 SDK 는 JavaScript 키 발급 + 플랫폼 도메인 등록이 필요하고,
 * 우리 개발계는 주소가 `http://{노드IP}:{NodePort}` 라 노드가 바뀔 때마다 등록이 깨진다. 우편번호
 * 서비스는 그 둘 다 없이 스크립트 한 줄로 뜬다.
 *
 * ## 왜 필요한가
 * 손으로 친 주소는 **지오코딩에서 조용히 실패한다**. 「강서구」 를 「강서」 로 적거나 「경기 평택」
 * 처럼 줄여 적으면 좌표가 안 나오고, 그 결과는 화면에 「자료 부족」 으로만 나타난다 — 어디가
 * 틀렸는지 아무도 모른다. 검색으로 고른 주소는 행정 자료 그대로라 그 실패가 없어진다.
 *
 * ## 스크립트는 누를 때 받는다
 * `index.html` 에 박으면 이 화면을 열지 않는 사람도 매번 외부 스크립트를 받는다. 처음 부를 때
 * 한 번만 넣고, 그 뒤로는 같은 약속을 돌려준다.
 *
 * ## ★ 팝업이 아니라 레이어로 연다 (2026-08-28 · 운영 사고)
 * 원래는 `open()` 이었다 — 서비스가 팝업 창을 띄우는 방식이다. 그런데 **운영에서 창이 열리기만
 * 하고 안이 비어 있었다.** 원인은 CSP 다.
 *
 * <p>서비스는 팝업 차단을 피하려 사용자 동작 안에서 `about:blank` 창을 **먼저** 열고, 그 안에
 * 문서를 써 넣은 뒤 **form submit 으로** 본체(`postcode.map.daum.net`)로 넘어간다. 그런데
 * `about:blank` 문서는 **연 문서의 CSP 를 물려받는다.** 우리 `form-action 'self'` 가 그 이동을
 * 막았고, 문서는 이미 쓰인 뒤라 **제목만 「Kakao Postcode Service」 로 잡힌 채 주소는
 * `about:blank` 로 멈췄다.** 스크립트는 200 으로 잘 받아 온 뒤라 네트워크 탭도 멀쩡해 보인다 —
 * 어디를 봐도 실패한 흔적이 없는 형태의 고장이었다.
 *
 * <p>레이어(`embed`)는 우리 페이지 안의 iframe 이라 `frame-src` 하나만 탄다. 팝업 차단·창 관리·
 * `form-action` 이 전부 사라지고, 실패하면 **이 화면의 콘솔에** 그대로 찍힌다. 창을 못 여는
 * 모바일에서도 같은 방식으로 뜬다.
 */

const SCRIPT_URL = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';

/** 우리가 쓰는 칸만 적는다. 서비스는 이보다 훨씬 많이 주지만 나머지는 받지 않는다. */
export type PostcodeResult = {
  /** 시/도 — 「서울특별시」 */
  sido: string;
  /** 시/군/구 — 「강서구」. <b>거주지는 여기까지만</b> 저장한다 */
  sigungu: string;
  /** 도로명 주소 전체. 현장 주소처럼 정밀도가 필요한 곳이 쓴다 */
  roadAddress: string;
  zonecode: string;
};

type DaumPostcodeData = {
  sido?: string;
  sigungu?: string;
  roadAddress?: string;
  jibunAddress?: string;
  address?: string;
  zonecode?: string;
};

declare global {
  interface Window {
    daum?: {
      Postcode: new (options: {
        oncomplete: (data: DaumPostcodeData) => void;
        /** `FORCE_CLOSE`(고르지 않고 닫음) · `COMPLETE_CLOSE`(고른 뒤 닫힘) */
        onclose?: (state: string) => void;
        width?: string;
        height?: string;
      }) => {
        /** 우리 화면 안의 요소에 그린다. 두 번째 인자를 주면 고른 뒤 스스로 지운다. */
        embed: (element: HTMLElement, options?: { autoClose?: boolean }) => void;
      };
    };
  }
}

let loading: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (window.daum?.Postcode) return Promise.resolve();
  if (loading) return loading;

  loading = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // 실패한 약속을 캐시에 남기면 다음 클릭도 영영 실패한다.
      loading = null;
      reject(new Error('주소 검색을 불러오지 못했습니다. 인터넷 연결을 확인해 주세요.'));
    };
    document.head.appendChild(script);
  });
  return loading;
}

/**
 * 덮개를 만든다. 공용 `Modal` 을 쓰지 않는 이유는 이 함수가 **컴포넌트가 아니라 약속**이라서다 —
 * 부르는 쪽(`await searchAddress()`)이 상태를 하나도 들지 않게 하려면 DOM 을 직접 세워야 한다.
 *
 * <p>쌓임 순서는 `.modal-overlay`(200)보다 위여야 한다. 이 검색은 <b>모달 안에서</b> 열린다
 * (현장 편집·마이페이지 거주지) — 같은 층이면 부모 모달 뒤로 숨는다.
 */
function buildOverlay(): { overlay: HTMLDivElement; host: HTMLDivElement; closeButton: HTMLButtonElement } {
  const overlay = document.createElement('div');
  overlay.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:300', 'display:flex',
    'align-items:center', 'justify-content:center', 'padding:16px',
    'background:rgba(15,23,42,0.55)',
  ].join(';');
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', '주소 검색');

  const panel = document.createElement('div');
  panel.style.cssText = [
    'background:white', 'border:1px solid var(--border)', 'border-radius:14px',
    'box-shadow:0 20px 60px rgba(0,0,0,0.15)', 'width:100%', 'max-width:520px',
    'height:min(600px,90vh)', 'display:flex', 'flex-direction:column', 'overflow:hidden',
  ].join(';');

  const header = document.createElement('div');
  header.style.cssText = [
    'display:flex', 'align-items:center', 'justify-content:space-between',
    'padding:14px 16px', 'border-bottom:1px solid var(--border)',
  ].join(';');

  const heading = document.createElement('h2');
  heading.textContent = '주소 검색';
  heading.style.cssText = 'font-size:15px;font-weight:600;color:var(--text-primary)';

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.textContent = '닫기';
  closeButton.setAttribute('aria-label', '주소 검색 닫기');
  closeButton.style.cssText = [
    'border:1px solid var(--border)', 'border-radius:8px', 'padding:4px 10px',
    'font-size:13px', 'background:transparent', 'color:var(--text-muted)', 'cursor:pointer',
  ].join(';');

  // 서비스가 여기에 iframe 을 넣는다. 높이가 0 이면 아무것도 안 보이므로 남는 높이를 전부 준다.
  const host = document.createElement('div');
  host.style.cssText = 'flex:1;min-height:0';

  header.append(heading, closeButton);
  panel.append(header, host);
  overlay.append(panel);
  return { overlay, host, closeButton };
}

/**
 * 주소 검색 창을 열고 고른 주소를 돌려준다. **사람이 닫으면 `null`** 이다 — 취소는 오류가 아니다.
 *
 * <p>레이어로 뜨므로 팝업 차단을 타지 않는다. 다만 스크립트를 받는 것은 여전히 처음 한 번이라,
 * 클릭 같은 사용자 동작 안에서 부르는 편이 기다림이 짧다.
 */
export async function searchAddress(): Promise<PostcodeResult | null> {
  await loadScript();

  return new Promise((resolve) => {
    const { overlay, host, closeButton } = buildOverlay();
    let settled = false;

    const finish = (value: PostcodeResult | null) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('keydown', onKeyDown, true);
      overlay.remove();
      resolve(value);
    };

    /*
      Esc 를 <b>가로채는 단계</b>에서 잡고 전파를 끊는다. 공용 `Modal` 도 window 에서 Esc 를 듣는데
      (거품 단계), 그대로 두면 이 검색을 닫으려 누른 Esc 가 **부모 모달까지 함께 닫는다** —
      현장 편집 중이던 내용이 통째로 사라진다. 이 덮개는 `Modal` 의 열림 스택 밖이라 그쪽의
      「맨 위 하나만」 규칙이 우리를 지켜 주지 못한다.
     */
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      event.stopImmediatePropagation();
      finish(null);
    }

    closeButton.addEventListener('click', () => finish(null));
    window.addEventListener('keydown', onKeyDown, true);
    document.body.appendChild(overlay);

    new window.daum!.Postcode({
      oncomplete: (data) => {
        finish({
          sido: data.sido ?? '',
          sigungu: data.sigungu ?? '',
          // 도로명이 없는 주소(지번만 있는 곳)가 있다. 그때는 지번으로 물러선다.
          roadAddress: data.roadAddress || data.jibunAddress || data.address || '',
          zonecode: data.zonecode ?? '',
        });
      },
      // 고르고 닫히는 것(`COMPLETE_CLOSE`)도 여기를 타지만 그때는 이미 `finish` 가 끝나 있다.
      onclose: () => finish(null),
      width: '100%',
      height: '100%',
    }).embed(host, { autoClose: true });
  });
}
